import React, { useEffect, useState } from 'react';
import { WhatsAppButton } from '../../components/WhatsApp.jsx';
import { useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { formatDate, placeName } from '../../format.js';
import ListingCard from '../../components/ListingCard.jsx';
import { displayValue, placeLabel } from '../../companyOptions.js';
import NotFound from '../site/NotFound.jsx';
import { useCategoryTree, nodeName } from '../../categoryTree.js';
import { displayPhone } from '../../phone.js';
import StartChatButton from '../../chat/StartChatButton.jsx';
import { Avatar, langName } from '../workspace/ws.jsx';

// Public company profile: logo, description, location, the approved
// company details (never bankers, sales, business number, phone or street
// address) and approved listings. No email or phone — buyers use the
// contact form on each listing.
export default function SellerProfile() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null); setMissing(false);
    api(`/marketplace/sellers/${encodeURIComponent(slug)}`).then((d) => alive && setData(d)).catch(() => alive && setMissing(true));
    return () => { alive = false; };
  }, [slug]);
  useEffect(() => { if (data) document.title = `${data.seller.companyName} | ${t('brand.name')}`; }, [data, t]);

  if (missing) return <NotFound />;
  if (!data) return <div className="page-loading">{t('common.loading')}</div>;
  const { seller, listings, company, documents = [], team = [] } = data;
  const place = placeName(seller, t);

  return (
    <>
      <section className={`page-hero${seller.coverUrl ? ' seller-hero-cover' : ''}`} style={seller.coverUrl ? { backgroundImage: `linear-gradient(rgba(11,31,56,0.55), rgba(11,31,56,0.75)), url("${seller.coverUrl}")` } : undefined}>
        <div className="container seller-hero">
          {seller.logoUrl ? <img className="seller-logo seller-logo-lg" src={seller.logoUrl} alt="" /> : <span className="seller-logo seller-logo-lg seller-logo-empty" aria-hidden="true">{seller.companyName.slice(0, 1)}</span>}
          <div>
            <h1>{seller.companyName}{seller.verified && <span className="verified-badge" title={t('seller.verifiedHint')}>✓ {t('seller.verified')}</span>}</h1>
            {company?.brand_tagline && <p className="seller-tagline">{company.brand_tagline}</p>}
            <p className="page-hero-meta">{[place, t('listing.memberSince', { date: formatDate(seller.memberSince, lang) })].filter(Boolean).join(' · ')}</p>
            <div className="seller-hero-chat"><StartChatButton sellerSlug={seller.slug} ownerSlug={seller.slug} label={t('chat.sendMessage')} className="btn btn-primary" /></div>
          </div>
        </div>
      </section>
      <section className="section section-tight">
        <div className="container">
          {company?.description_html
            ? <div className="rich-text seller-description" dangerouslySetInnerHTML={{ __html: company.description_html }} />
            : seller.description && <p className="seller-description">{seller.description}</p>}
          {company && <CompanyDetails c={company} />}
          {company && <PublicContact c={company} />}
          {documents.length > 0 && (
            <section className="card seller-docs">
              <h3 className="card-title">{t('seller.documents')}</h3>
              <ul className="seller-doc-list">
                {documents.map((d) => (
                  <li key={d.id}><a href={d.url} target="_blank" rel="noreferrer">📄 {t(`documentsPage.types.${d.type}`)}{d.number ? ` — ${d.number}` : ''}</a> <span className="badge badge-success">✓ {t('seller.verifiedDoc')}</span>
                    {d.expiresOn && <span className="muted small"> · {t('documentsPage.expires', { date: formatDate(d.expiresOn, lang) })}</span>}</li>
                ))}
              </ul>
            </section>
          )}
          {team.length > 0 && (
            <section className="seller-team">
              <h2>{t('seller.team')}</h2>
              <ul className="pub-team">
                {team.map((m) => (
                  <li key={m.id}>
                    <Avatar name={m.name} photoUrl={m.photoUrl} size={56} />
                    <div className="ws-grow">
                      <strong>{m.name}</strong>
                      {m.title && <div className="small">{m.title}</div>}
                      {(m.languages.length > 0 || m.office) && <div className="muted small">{[m.office, m.languages.map((l) => langName(l, lang)).join(', ')].filter(Boolean).join(' · ')}</div>}
                      <StartChatButton sellerSlug={seller.slug} ownerSlug={seller.slug} repId={m.id} label={t('seller.contactRep')} className="btn btn-link btn-sm" />
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <h2>{t('seller.listings', { name: seller.companyName })}</h2>
          {listings.length ? (
            <div className="listing-grid">{listings.map((l) => <ListingCard key={l.id} listing={l} showSeller={false} />)}</div>
          ) : <p className="muted">{t('seller.noListings')}</p>}
        </div>
      </section>
    </>
  );
}

// Contact details the member chose to show (Coordonnées > Public).
function PublicContact({ c }) {
  const { t, lang } = useI18n();
  const rows = [
    c.contact_email && [t('contactPage.email'), <a href={`mailto:${c.contact_email}`}>{c.contact_email}</a>],
    c.phone && [t('company.fields.phone'), <a href={`tel:${c.phone.replace(/[^+\d]/g, '')}`}>{c.phone}</a>],
    c.whatsapp && ['WhatsApp', <a href={`https://wa.me/${c.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer">{c.whatsapp}</a>],
    c.address_line && [t('company.fields.address'), [c.address_line, c.city, c.region, c.postal_code, displayValue('country', c.country, t, lang)].filter(Boolean).join(', ')],
    ...(c.addresses || []).map((a) => [t(`company.addressKinds.${a.kind}`), [a.line, a.city, a.postal, a.country && displayValue('country', a.country, t, lang)].filter(Boolean).join(', ')]),
  ].filter(Boolean);
  const people = c.contacts || [];
  if (!rows.length && !people.length) return null;
  return (
    <section className="card seller-contact">
      <h3 className="card-title">{t('seller.contactDetails')}</h3>
      {rows.length > 0 && <dl className="facts">{rows.map(([k, v], i) => <React.Fragment key={i}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}</dl>}
      {c.whatsapp && <p><WhatsAppButton number={c.whatsapp} small text={t('wa.sellerMessage', { url: window.location.href.split('?')[0] })} /></p>}
      {people.length > 0 && (
        <ul className="seller-people">
          {people.map((p, i) => <li key={i}><strong>{p.name}</strong>{p.title && ` — ${p.title}`}<br /><span className="small">{[p.email && <a key="e" href={`mailto:${p.email}`}>{p.email}</a>, p.phone].filter(Boolean).reduce((a, x, j) => (j ? [...a, ' · ', x] : [x]), [])}</span></li>)}
        </ul>
      )}
    </section>
  );
}

function CompanyDetails({ c }) {
  const { t, lang } = useI18n();
  const tree = useCategoryTree();
  const v = (f) => displayValue(f, c[f], t, lang, c);
  const sectors = (c.sectors || []).map((id) => tree.get(id)).filter(Boolean);
  const social = Object.entries(c.social || {});
  const about = ['supplier_of', 'buyer_of', 'services'].filter((f) => c[f]);
  const contact = [c.contact_person, c.designation].filter(Boolean).join(', ');
  const facts = [
    ['primary_business', v('primary_business')],
    ['year_established', v('year_established')],
    ['employees', v('employees')],
    ['legal_name', c.legal_name !== c.trade_name ? v('legal_name') : ''],
    ['brand_name', v('brand_name')],
    ['division_name', v('division_name')],
    ['certifications', v('certifications')],
    ['contact_person', contact],
    ['country', v('country')],
  ].filter(([, x]) => x);
  const markets = ['markets', 'domestic_markets', 'exported_to', 'imported_from'].filter((f) => c[f]?.length);
  if (!about.length && !facts.length && !markets.length && !c.website && !sectors.length && !social.length) return null;
  return (
    <div className="company-details">
      {sectors.length > 0 && (
        <div className="seller-sectors">
          <span className="field-label">{t('companyPage.sectors')}</span>
          <ul className="chip-list">{sectors.map((n) => <li key={n.id} className="chip"><a href={`/marketplace?category=${encodeURIComponent(n.slug)}`}>{nodeName(n, lang)}</a></li>)}</ul>
        </div>
      )}
      {about.length > 0 && (
        <div className="about-grid">
          {about.map((f) => (
            <section key={f} className="about-card">
              <h3>{t(`seller.about.${f}`)}</h3>
              <p className="pre-line">{c[f]}</p>
            </section>
          ))}
        </div>
      )}
      <div className="facts-grid">
        {(facts.length > 0 || c.website || social.length > 0) && (
          <section className="card">
            <h3 className="card-title">{t('seller.facts')}</h3>
            <dl className="facts">
              {facts.map(([f, x]) => <React.Fragment key={f}><dt>{f === 'contact_person' ? t('seller.contact') : t(`company.fields.${f}`)}</dt><dd>{x}</dd></React.Fragment>)}
              {c.website && <><dt>{t('company.fields.website')}</dt><dd><a href={c.website} target="_blank" rel="noreferrer noopener nofollow">{c.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a></dd></>}
              {social.map(([k, u]) => <React.Fragment key={k}><dt>{t(`company.social.${k}`)}</dt><dd>{k === 'wechat' ? u : <a href={u} target="_blank" rel="noreferrer noopener nofollow">{u.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</a>}</dd></React.Fragment>)}
            </dl>
          </section>
        )}
        {markets.length > 0 && (
          <section className="card">
            <h3 className="card-title">{t('seller.markets')}</h3>
            {markets.map((f) => (
              <div key={f} className="market-row">
                <span className="field-label">{t(`company.fields.${f}`)}</span>
                <ul className="chip-list">
                  {c[f].map((code) => <li key={code} className="chip">{f === 'domestic_markets' ? t(`provinces.${code}`) : placeLabel(code, t, lang)}</li>)}
                </ul>
              </div>
            ))}
          </section>
        )}
      </div>
    </div>
  );
}
